import { useCallback, useEffect, useState } from "react";
import api from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useCustomer } from "../context/CustomerContext";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import RowActions from "../components/RowActions";
import { usePublishScreenData } from "../utils/aiScreenContext";
import ActionButton from "../components/ActionButton";
import { formatDate, formatDateTime } from "../utils/datetime";

// Fleet-wide certificate expiry view (Monitoring & Alerts). Lists every
// X.509 cert the agents report from LocalMachine My / WebHosting, so a tech can
// spot expired / expiring certs across all devices at a glance.

type Cert = {
  id: number;
  device_id: number;
  device_hostname?: string | null;
  customer_name?: string | null;
  thumbprint: string;
  subject?: string | null;
  issuer?: string | null;
  friendly_name?: string | null;
  store: string;
  not_after?: string | null;
  days_until_expiry?: number | null;
  has_private_key: boolean;
};

const WINDOWS = [
  { v: "", l: "All certificates" },
  { v: "90", l: "Expiring ≤ 90 days" },
  { v: "30", l: "Expiring ≤ 30 days" },
  { v: "14", l: "Expiring ≤ 14 days" },
  { v: "7", l: "Expiring ≤ 7 days" },
];

function statusOf(days: number | null | undefined): { label: string; cls: string } {
  if (days == null) return { label: "unknown", cls: "bg-gray-100 text-gray-600 dark:bg-gray-700/40 dark:text-gray-300" };
  if (days < 0) return { label: "expired", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" };
  if (days <= 7) return { label: "critical", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300" };
  if (days <= 14) return { label: "warning", cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300" };
  if (days <= 30) return { label: "soon", cls: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300" };
  return { label: "ok", cls: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300" };
}

export default function Certificates() {
  const [certs, setCerts] = useState<Cert[]>([]);
  const [within, setWithin] = useState("90");
  const [store, setStore] = useState("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const certSort = useSort("status", "asc");
  const { user } = useAuth();
  const { currentCustomerId } = useCustomer();
  const isAdmin = user?.role === "admin";
  const canRenew = isAdmin || user?.role === "technician";

  const doRescan = async () => {
    setErr("");
    try {
      const r = await api.post(
        "/api/v1/certificates/rescan",
        {},
        { params: currentCustomerId != null ? { customer_id: currentCustomerId } : {} },
      );
      const n = r.data?.queued ?? 0;
      setMsg(
        n > 0
          ? `Rescan queued to ${n} online device${n === 1 ? "" : "s"}. They'll re-report certificates in ~1-2 min — then click Refresh. (Requires agent 1.0.66+.)`
          : "No online devices to rescan in this scope.",
      );
    } catch (e: any) {
      setErr(e.response?.data?.detail || "Rescan failed");
    }
  };

  // Publish the visible cert rows so the global AI Assist can act on
  // "the ones on my screen".
  usePublishScreenData(
    "Certificates",
    certs,
    `filter: expiring within ${within || "any"} days, store ${store || "all"}`,
  );

  const doRemove = async (c: Cert) => {
    if (
      !window.confirm(
        `Remove this certificate from ${c.device_hostname || "#" + c.device_id}?\n\n` +
          `${c.subject || c.thumbprint}\nStore: ${c.store}\n\n` +
          `WARNING: if this cert is in use (TLS/RDP/LDAPS) this WILL break that service. ` +
          `There's no in-use data — only remove certs you know are safe.`,
      )
    )
      return;
    try {
      await api.post(`/api/v1/certificates/${c.id}/remove`);
      setMsg(`Removal queued for "${c.subject || c.thumbprint}" on ${c.device_hostname || "#" + c.device_id}.`);
    } catch (e: any) {
      setErr(e.response?.data?.detail || "Remove failed");
    }
  };

  const doRenew = async (c: Cert) => {
    if (
      !window.confirm(
        `Trigger AD certificate auto-enrollment on ${c.device_hostname || "#" + c.device_id}?\n\n` +
          `This renews domain-issued auto-enrolled certs nearing expiry (certutil -pulse). ` +
          `It won't renew public/ACME or manually-issued certs.`,
      )
    )
      return;
    try {
      await api.post(`/api/v1/certificates/${c.id}/renew`);
      setMsg(`Auto-enrollment renewal triggered on ${c.device_hostname || "#" + c.device_id}.`);
    } catch (e: any) {
      setErr(e.response?.data?.detail || "Renew failed");
    }
  };

  const load = useCallback(() => {
    setLoading(true);
    setErr("");
    const qs = new URLSearchParams();
    if (within) qs.set("expiring_within_days", within);
    if (store) qs.set("store", store);
    api
      .get(`/api/v1/certificates?${qs.toString()}`)
      .then((r) => setCerts(r.data || []))
      .catch((e) => setErr(e.response?.data?.detail || e.message))
      .finally(() => setLoading(false));
  }, [within, store]);
  useEffect(load, [load]);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Certificates</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          X.509 certificates across the fleet (LocalMachine personal + web-hosting stores). Set
          expiry alert thresholds under{" "}
          <a href="/monitoring/settings" className="text-blue-600 hover:underline">
            Settings
          </a>
          .
        </p>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={within}
          onChange={(e) => setWithin(e.target.value)}
          className="border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm dark:bg-gray-900"
        >
          {WINDOWS.map((w) => (
            <option key={w.v} value={w.v}>
              {w.l}
            </option>
          ))}
        </select>
        <select
          value={store}
          onChange={(e) => setStore(e.target.value)}
          className="border border-gray-300 dark:border-gray-600 rounded-lg px-2 py-1.5 text-sm dark:bg-gray-900"
        >
          <option value="">All stores</option>
          <option value="My">Personal (My)</option>
          <option value="WebHosting">Web Hosting</option>
        </select>
        <button
          onClick={load}
          className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          Refresh
        </button>
        {canRenew && (
          <ActionButton onAction={() => doRescan()} variant="raw" className="text-sm bg-blue-600 text-white rounded-lg px-3 py-1.5 hover:bg-blue-700" title="Ask online devices to re-collect and re-report their certificates now" pendingLabel="Scanning…">
            Rescan devices
          </ActionButton>
        )}
      </div>

      {err && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-4 py-2 rounded-lg text-sm">
          {err}
        </div>
      )}
      {msg && (
        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300 px-4 py-2 rounded-lg text-sm">
          {msg}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500 dark:text-gray-400 text-xs uppercase">
            <tr>
              <SortableTh label="Status" sortKey="status" sort={certSort.sort} onSort={certSort.toggle} />
              <SortableTh label="Subject" sortKey="subject" sort={certSort.sort} onSort={certSort.toggle} />
              <SortableTh label="Device" sortKey="device" sort={certSort.sort} onSort={certSort.toggle} hide="hidden lg:table-cell" />
              <SortableTh label="Customer" sortKey="customer" sort={certSort.sort} onSort={certSort.toggle} hide="hidden md:table-cell" />
              <SortableTh label="Store" sortKey="store" sort={certSort.sort} onSort={certSort.toggle} hide="hidden xl:table-cell" />
              <SortableTh label="Expires" sortKey="expires" sort={certSort.sort} onSort={certSort.toggle} hide="hidden sm:table-cell" />
              {canRenew && <th className="px-4 py-2 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {loading ? (
              <tr>
                <td colSpan={canRenew ? 7 : 6} className="px-4 py-8 text-center text-gray-400">
                  Loading…
                </td>
              </tr>
            ) : certs.length === 0 ? (
              <tr>
                <td colSpan={canRenew ? 7 : 6} className="px-4 py-8 text-center text-gray-400">
                  No certificates reported yet. Devices on agent 1.0.21+ report their machine certs
                  on the hourly inventory check-in.
                </td>
              </tr>
            ) : (
              sortRows(certs, certSort.sort, (c, key) => {
                switch (key) {
                  case "status": return c.days_until_expiry ?? Number.MAX_SAFE_INTEGER;
                  case "subject": return (c.friendly_name || c.subject || c.thumbprint || "").toLowerCase();
                  case "device": return (c.device_hostname || `#${c.device_id}`).toLowerCase();
                  case "customer": return (c.customer_name || "").toLowerCase();
                  case "store": return (c.store || "").toLowerCase();
                  case "expires": return c.not_after ? Date.parse(c.not_after) : 0;
                  default: return 0;
                }
              }).map((c) => {
                const s = statusOf(c.days_until_expiry);
                const details = [
                  c.subject ? `Subject: ${c.subject}` : null,
                  c.friendly_name ? `Friendly name: ${c.friendly_name}` : null,
                  c.issuer ? `Issuer: ${c.issuer}` : null,
                  `Store: ${c.store}`,
                  c.not_after ? `Expires: ${formatDateTime(c.not_after)}` : null,
                  c.days_until_expiry != null
                    ? `Days until expiry: ${c.days_until_expiry}`
                    : null,
                  `Private key: ${c.has_private_key ? "yes" : "no"}`,
                  `Thumbprint: ${c.thumbprint}`,
                ]
                  .filter(Boolean)
                  .join("\n");
                return (
                  <tr
                    key={c.id}
                    className="text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700/40"
                    title={details}
                  >
                    <td className="px-4 py-2">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${s.cls}`}
                      >
                        {s.label}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <div
                        className="font-medium max-w-[9rem] sm:max-w-none truncate"
                        title={c.friendly_name || c.subject || c.thumbprint || ""}
                      >
                        {c.friendly_name || c.subject || c.thumbprint}
                      </div>
                      {/* The Expires column is hidden below sm, and an expiry
                          date is the entire point of this page - so it rides
                          under the subject instead of being dropped. */}
                      <div className="sm:hidden text-[11px] text-gray-500 dark:text-gray-400">
                        {c.not_after ? formatDate(c.not_after) : "no expiry date"}
                        {c.days_until_expiry != null
                          ? c.days_until_expiry < 0
                            ? ` · expired ${Math.abs(c.days_until_expiry)}d ago`
                            : ` · ${c.days_until_expiry}d left`
                          : ""}
                      </div>
                      {c.issuer && (
                        <div className="text-xs text-gray-400">issued by {c.issuer}</div>
                      )}
                    </td>
                    <td className="px-4 py-2 hidden lg:table-cell">
                      <a
                        href={`/devices/${c.device_id}`}
                        className="text-blue-600 hover:underline"
                      >
                        {c.device_hostname || `#${c.device_id}`}
                      </a>
                    </td>
                    <td className="px-4 py-2 hidden md:table-cell">{c.customer_name || "—"}</td>
                    <td className="px-4 py-2 hidden xl:table-cell">{c.store}</td>
                    <td className="px-4 py-2 hidden sm:table-cell">
                      {c.not_after ? formatDate(c.not_after) : "—"}
                      {c.days_until_expiry != null && (
                        <span className="text-xs text-gray-400 ml-1">
                          ({c.days_until_expiry < 0
                            ? `${Math.abs(c.days_until_expiry)}d ago`
                            : `${c.days_until_expiry}d`}
                          )
                        </span>
                      )}
                    </td>
                    {canRenew && (
                      <td className="px-4 py-2 text-right">
                        <div className="sm:hidden flex justify-end">
                          <RowActions
                            actions={[
                              { label: "Renew", onClick: () => doRenew(c) },
                              {
                                label: "Remove",
                                onClick: () => doRemove(c),
                                danger: true,
                                // Mirrors the desktop row, which only offers
                                // Remove for a cert it can actually remove.
                                hidden: !c.device_id,
                              },
                            ]}
                          />
                        </div>
                        <div className="hidden sm:block whitespace-nowrap">
                        <button
                          onClick={() => doRenew(c)}
                          className="text-xs text-blue-600 hover:underline mr-3"
                          title="Trigger AD auto-enrollment renewal on this device (certutil -pulse)"
                        >
                          Renew
                        </button>
                        {isAdmin && (
                          <button
                            onClick={() => doRemove(c)}
                            className="text-xs text-red-600 hover:underline"
                            title="Delete this certificate from the device's store"
                          >
                            Remove
                          </button>
                        )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
